/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import { useEffect } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Typography } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomCreateReferralCode } from '@root/features/referral/atoms/reatom-create-referral-code'
import { createReferralCodeDialogOpenAtom } from '@root/features/referral/atoms/reatom-referral-dialogs'
import { useTranslation } from '@root/shared/localization'
import { Drawer, Separator } from '@root/shared/ui-kit'

import { ReferralCodeForm } from '../../desktop/common/referral-code-form'
import { ReferralPointsInfo } from '../../desktop/common/referral-points-info'

import type { ReferralCodeSubmitValues } from '../../desktop/common/referral-code-form'

const CREATE_REFERRAL_CODE_PRICE = 0.0098

export const CreateReferralCodeDrawer = () => {
  const [open, setOpen] = useAtom(createReferralCodeDialogOpenAtom)

  return (
    <AnimatePresence>
      {open ? <Inner open={open} onOpenChange={setOpen} /> : undefined}
    </AnimatePresence>
  )
}

type InnerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const Inner = ({ open, onOpenChange }: InnerProps) => {
  const { t } = useTranslation()
  const [selectedNetwork] = useAtom(selectedNetworkAtom)
  const model = useMemo(() => reatomCreateReferralCode(), [])
  const [requestState] = useAtom(model.requestState)
  const execute = useAction(model.create)

  const handleSubmit = (values: ReferralCodeSubmitValues) => {
    execute({
      referralCode: values.referralCode,
      walletId: values.wallet.id,
    })
  }

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onOpenChange(false)
    }
  }, [requestState, onOpenChange])

  return (
    <Root open={open} onOpenChange={onOpenChange} backgroundGradient="primary">
      <Content>
        <Header>
          <Title>{t('referral.create-title')}</Title>
          <SubTitle>
            <Trans
              i18nKey="referral.create-subtitle"
              components={[<strong key={0} />]}
            />
          </SubTitle>
          <ReferralPointsInfo />
          <Separator />
          <Price>
            <PriceTitle>{t('referral.create-price-title')}</PriceTitle>
            <PriceCost>
              {CREATE_REFERRAL_CODE_PRICE}{' '}
              {Network.toDisplayString(selectedNetwork).toUpperCase()}
            </PriceCost>
          </Price>
        </Header>
        <Body>
          <Separator />
          <Form
            device="mobile"
            onCancel={() => onOpenChange(false)}
            state={requestState}
            onSubmit={handleSubmit}
          />
        </Body>
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)``

const Content = styled.div`
  padding-bottom: var(--safe-bottom);
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Title = styled(Typography).attrs({
  variant: 'h4',
  component: 'h2',
})`
  font-size: 18px;
  line-height: 140%;
  color: var(--text-12);
`
const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  text-align: center;
  gap: 16px;
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 12px;
  text-align: center;
  gap: 16px;
  background: radial-gradient(
    106.14% 158.88% at 50% -46.89%,
    var(--backgroundA-4) 0%,
    var(--backgroundA-1) 100%
  );
  backdrop-filter: blur(16px);
`

const SubTitle = styled(Typography).attrs({
  variant: 'body3-medium',
  component: 'h3',
})`
  color: var(--textA-9);

  strong {
    color: var(--textA-11);
  }
`

const Price = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PriceTitle = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'div',
})`
  color: var(--textA-10);
`

const PriceCost = styled(Typography).attrs({
  variant: 'body2-semi-bold',
  component: 'div',
})``

const Form = styled(ReferralCodeForm)`
  padding: 0 12px;
`
